import "./BookDetailsPage.css";

import { Column, Container, Content } from "components/trunx";
import { useAuth } from "contexts/AuthContext";
import { onSnapshot } from "firebase/firestore";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Book, BookPriceType, BookStatus, BookType, VippsPayment, VippsPaymentState } from "shared";
import VippsService from "services/VippsService";

import { hasBookTypeAnyFiles } from "../../../common/BookUtils";
import useUrlQueries from "../../../common/hooks/useQuery";
import LoadingIndicator from "../../../components/LoadingIndicator";
import PageTemplate from "../../../components/pagetemplate/PageTemplate";
import { logAuthorizedPurchase } from "../../../services/analytics/purchaseAnalytics";
import useBookApi from "../../../services/firestore/hooks/useBookApi";
import { AnalyticsApi } from "../../../services/firestore/queries/queriesCommon";
import { MetaPurchase, trackMetaEvent, trackMetaPurchase } from "../../../services/meta/MetaPixel";
import { useBooleanFeatureFlag } from "../../../services/remoteconfig/useBooleanFeatureFlag";
import BookDetails from "./BookDetails";
import BookDetailsProvider from "./BookDetailsContext";
import ReceiptPage from "./purchase/ReceiptPage";

type UnsubscribeFn = () => void;
interface BookDetailsContainerProps {
    onClose?: () => void;
}

function getViewedBookType(query, book?: Book | null) {
    const typeFromQuery = query.get("type");
    if (typeFromQuery) return typeFromQuery == "audio" ? BookType.AUDIO : BookType.EBOOK;
    return book?.bookType == BookType.PODCAST ? BookType.PODCAST : BookType.EBOOK;
}

export default function BookPurchaseDetailsPage(props: BookDetailsContainerProps) {
    return (
        <React.Suspense
            fallback={
                <Column pt6 isFlex isFlexDirectionColumn isJustifyContentCenter isAlignItemsCenter isFlexGrow1>
                    <LoadingIndicator size="XL" />
                    <Content>Laster bok</Content>
                </Column>
            }
        >
            <PageTemplate>
                <Container>
                    <BookPurchaseDetailsPageContainer {...props} />
                </Container>
            </PageTemplate>
        </React.Suspense>
    );
}
function BookPurchaseDetailsPageContainer({ onClose }: BookDetailsContainerProps) {
    const [transactionStatus, setTransactionStatus] = useState<VippsPaymentState>();
    const [snapshotUnsubscribeFn, setSnapshotUnsubscribeFn] = useState<UnsubscribeFn>();
    const viewedBookId = useRef<string>();
    const [authorizedPurchase, setAuthorizedPurchase] = useState<
        Omit<MetaPurchase, "bookId"> & { bookId?: string; bookType?: BookType }
    >();

    const query = useUrlQueries();
    const { currentUser, hasRole, loginWithOrderId } = useAuth();
    const { bookUrl } = useParams<{ bookUrl: string }>();
    const orderId = query.get("orderid");
    const bookId = query.get("id");

    const { data: book, isLoading, isError } = useBookApi().bookByUrl(bookUrl, bookId);
    const bookType = getViewedBookType(query, book);
    const pixelFlag = useBooleanFeatureFlag("publizm_meta_pixel_enabled");

    useEffect(() => {
        if (pixelFlag.status !== "ready" || !pixelFlag.enabled || !book || isLoading || orderId || !book.id || viewedBookId.current === book.id) return;
        if (book.status !== BookStatus.PUBLISHED || book.source !== "publizm") return;
        const purchasable = [
            [BookType.EBOOK, BookPriceType.EBOOK],
            [BookType.AUDIO, BookPriceType.AUDIOBOOK],
        ] as const;
        if (!purchasable.some(([type, priceType]) =>
            hasBookTypeAnyFiles(type, book) && book.prices?.some((price) => price.type === priceType)
        )) return;
        if (trackMetaEvent("ViewContent", { content_ids: [book.id], content_type: "product" })) {
            viewedBookId.current = book.id;
        }
    }, [book, isLoading, orderId, pixelFlag.status, pixelFlag.enabled]);

    useEffect(() => {
        // Waits for the book so the title is current; the snapshot callback only sees the book from subscription time.
        if (!authorizedPurchase || !book) return;
        logAuthorizedPurchase({
            ...authorizedPurchase,
            bookId: authorizedPurchase.bookId ?? book.id ?? "",
            title: book.title,
            bookType: authorizedPurchase.bookType ?? book.bookType ?? bookType,
        });
    }, [authorizedPurchase, book, bookType]);

    useEffect(() => {
        // Retried when the flag is confirmed, because AUTHORIZED can arrive before Remote Config after the Vipps return.
        if (!authorizedPurchase || pixelFlag.status !== "ready" || !pixelFlag.enabled) return;
        const bookId = authorizedPurchase.bookId ?? book?.id;
        if (bookId) void trackMetaPurchase({ ...authorizedPurchase, bookId });
    }, [authorizedPurchase, book?.id, pixelFlag.status, pixelFlag.enabled]);

    useEffect(() => {
        // if (orderId) {
        //     subscribeToTransactionSnapshot(orderId)
        //         .then((fn) => {
        //             setSnapshotUnsubscribeFn(() => fn);
        //         })
        //         .catch(console.error);
        // }
        // if (orderId && !currentUser) {
        //     loginWithOrderId(orderId)
        // }

        if (orderId && !currentUser) {
            loginWithOrderId(orderId).then((userId) => {
                if (!userId) {
                    setTransactionStatus(VippsPaymentState.ABORTED);
                    return
                };
                subscribeToTransactionSnapshot(orderId, userId)
                    .then((fn) => {
                        setSnapshotUnsubscribeFn(() => fn);
                    })
                    .catch(console.error);
            })
        } else if (orderId && currentUser) {
            subscribeToTransactionSnapshot(orderId, currentUser.uid)
                .then((fn) => {
                    setSnapshotUnsubscribeFn(() => fn);
                })
                .catch(console.error);
        }
    }, []);

    useEffect(() => {
        if (transactionStatus && transactionStatus == VippsPaymentState.CAPTURE) {
            snapshotUnsubscribeFn?.();
        }
    }, [transactionStatus]);

    useEffect(() => {
        if (book) {
            AnalyticsApi.logEvent("show_book_details", { title: book.title, bookId: book.id });
            AnalyticsApi.logViewItem(book.id ?? "ukjent", book.title, book.bookType);
        }
    }, []);

    async function subscribeToTransactionSnapshot(orderId: string, userId: string): Promise<UnsubscribeFn> {
        return onSnapshot(
            new VippsService().getVippsPaymentInfoDocumentRefByOrderIdAndUserIdQuery(orderId, userId),
            { includeMetadataChanges: true },
            (snap) => {
                if (snap.empty) return;
                const data = snap.docs[0].data() as VippsPayment;
                if (data.paymentState !== transactionStatus) {
                    setTransactionStatus(data.paymentState);
                }
                if (data.paymentState == VippsPaymentState.AUTHORIZED) {
                    // transactionInfo.amount is NOK; analytics receive it unchanged.
                    const valueNok = data.transactionInfo?.amount ?? 0;
                    setAuthorizedPurchase((current) =>
                        current?.orderId === data.orderId
                            ? current
                            : {
                                  orderId: data.orderId,
                                  bookId: data.productInfo?.bookId,
                                  bookType: data.productInfo?.bookType,
                                  valueNok,
                              }
                    );
                }
            }
        );
    }

    if (isError || (!isLoading && !book)) {
        return <Container>{"Fant ikke bok med tittel " + bookUrl}</Container>;
    }

    console.log("transactionStatus", transactionStatus, "orderId", orderId, "isLoading", isLoading, "book", book);
    if (!book || isLoading || (orderId != undefined && transactionStatus == VippsPaymentState.CREATED)) {
        return <LoadingIndicator />;
    }

    if (orderId && !currentUser && !transactionStatus) {
        return <div>
            <LoadingIndicator label="Logger deg inn. Vennligst vent" />
        </div>
    }
    if (orderId && transactionStatus === VippsPaymentState.AUTHORIZED || transactionStatus == VippsPaymentState.CAPTURED) {
        return <ReceiptPage book={book} loggingIn={orderId != null && currentUser == null} />;
    }
    return (
        <BookDetailsProvider bookId={book.id!} book={book} type={bookType}>
            <BookDetails onClose={onClose} />
        </BookDetailsProvider>
    );
}
